import { observer } from 'mobx-react';
import { useContext, useState, useEffect } from 'react';
import {
  PlusIcon,
  CheckCircleIcon,
  ClockIcon,
  BookOpenIcon,
  AcademicCapIcon,
  ArrowDownTrayIcon,
  GlobeAltIcon,
  LockClosedIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import { Window } from '@execonline-inc/browser-window.private';
import { L } from '@execonline-inc/translations';
import { fromNullable, Maybe } from 'maybeasy';
import {
  ClientExperienceResource,
  ExperienceResource,
  ProductCollectionResource,
} from '../../../Types';
import ExperienceVideoState from '../../../../../ExperienceVideoState';
import { getOfferingTypeLabel, getOfferingTypeIcon } from './offeringTypeHelpers';
import { FUTURE_LICENSE_TINT_CLASSES, LicenseStarts } from '../../../LicenseTracker/FutureLicense';
import { trackIntercomEvent } from '../../../../../../../../services/intercomTrackingService';
import { findLinkBy } from '../../../../../../../../Fetch';
import { T, TranslationsContext, translation } from '../../../../../../../../Translations';

interface Props {
  course: Maybe<ClientExperienceResource>;
  productCollection: ProductCollectionResource;
  index: number;
  sectionName: string;
  addedCourseIds: number[];
  onToggleCourse: (courseId: number) => void;
}

function CourseDetailPanel(props: Props) {
  return props.course.cata({
    Nothing: () => <EmptyDetailPanel />,
    Just: (course) => <CourseDetailContent {...props} course={course} />,
  });
}

function EmptyDetailPanel() {
  return (
    <div className="flex-1 flex items-center justify-center p-8 bg-background">
      <div className="text-center max-w-md">
        <div className="w-20 h-20 mx-auto mb-6 bg-muted rounded-full flex items-center justify-center">
          <BookOpenIcon className="w-10 h-10 text-muted-foreground" />
        </div>
        <h3 className="mb-2">
          <T kind="Select a course" />
        </h3>
        <p className="text-muted-foreground" style={{ fontSize: 'var(--text-sm)' }}>
          <T kind="Choose a course from the list to view full details" />
        </p>
      </div>
    </div>
  );
}

interface ContentProps extends Omit<Props, 'course'> {
  course: ClientExperienceResource;
}

function CourseDetailContent({
  course,
  productCollection,
  index,
  sectionName,
  addedCourseIds,
  onToggleCourse,
}: ContentProps) {
  const ts = useContext(TranslationsContext);
  // Track selected scheduled dates
  const [selectedDateIds, setSelectedDateIds] = useState<Set<number>>(new Set());

  // Select all dates by default when course changes
  // Note: Only triggers on course ID change (user selects different course),
  // not on date updates for the same course. This prevents resetting user's
  // date selections when course data refreshes.
  useEffect(() => {
    const scheduledDates = course.payload.program_instance_options?.scheduled || [];
    const allDateIds = new Set(scheduledDates.map((date) => date.id));
    setSelectedDateIds(allDateIds);
  }, [course]);

  const toggleDateSelection = (dateId: number) => {
    const isCurrentlySelected = selectedDateIds.has(dateId);
    trackIntercomEvent('Builder V2 - Course Catalog - Toggle Course Date', {
      courseId: course.payload.id,
      courseTitle: course.payload.title,
      dateId,
      action: isCurrentlySelected ? 'deselect' : 'select',
    });

    setSelectedDateIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(dateId)) {
        newSet.delete(dateId);
      } else {
        newSet.add(dateId);
      }
      return newSet;
    });
  };

  const {
    title,
    total_hours,
    offering_type,
    school_partner_name,
    competency_names,
    supported_languages,
    product_license_type_ids,
    program_instance_options,
  } = course.payload;

  const payload = course.payload;

  // Check if course is added to collection
  const isAdded = addedCourseIds.includes(course.payload.id);

  // Check if PDF download is available
  const hasPdfDownload = findLinkBy({ rel: 'download-overview-pdf' }, course.links).isJust();

  // Extract Maybe values
  const hours = total_hours.getOrElseValue(0);
  const weeks = payload.total_weeks.getOrElseValue(0);
  const schoolName = school_partner_name || '';
  const schoolLogoUri = payload.school_partner_logo_url
    .orElse(() => fromNullable(course.links.find((l) => l.rel === 'logo-on-white')?.href))
    .getOrElseValue('');
  const offeringType = offering_type;
  const languages = supported_languages || [];
  const isUnlicensed = !product_license_type_ids || product_license_type_ids.length === 0;
  const competencyList = competency_names || [];
  const scheduledDates = program_instance_options?.scheduled || [];

  const handleToggle = () => {
    trackIntercomEvent('Builder V2 - Course Catalog - Toggle Course from Detail', {
      courseId: course.payload.id,
      courseTitle: title,
      action: isAdded ? 'remove' : 'add',
    });
    onToggleCourse(course.payload.id);
  };

  const handleDownload = () => {
    trackIntercomEvent('Builder V2 - Course Catalog - Download PDF from Detail', {
      courseId: course.payload.id,
      courseTitle: title,
    });
    findLinkBy({ rel: 'download-overview-pdf' }, course.links).map((link) => {
      Window.openWindow(link.href, '_blank');
    });
  };

  // Get description from course (Maybe<string> type)
  const description = course.payload.description.getOrElseValue('');

  return (
    <div
      className="flex-1 flex flex-col bg-background"
      data-intercom-target="builder-v2-course-detail-panel"
    >
      {/* Header */}
      <div className="px-8 py-6 bg-muted/30 border-b border-border flex-shrink-0">
        <div className="flex gap-6">
          <div className="flex-1 min-w-0">
            {/* School Logo + Name */}
            {(schoolLogoUri || schoolName) && (
              <div className="flex items-center gap-2 mb-2">
                {/* DVLP-7604: logo-on-white asset - render clean (no grey box) and larger. */}
                {schoolLogoUri && (
                  <img
                    src={schoolLogoUri}
                    alt={translation('School partner logo')(ts)}
                    className="h-7 object-contain"
                    style={{ maxWidth: '120px' }}
                  />
                )}
                {schoolName && (
                  <p
                    className="text-primary"
                    style={{
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-weight-semibold)',
                    }}
                  >
                    {schoolName}
                  </p>
                )}
              </div>
            )}

            {/* Course Title */}
            <h2
              className="mb-4"
              style={{ wordBreak: 'break-word', fontWeight: 'var(--font-weight-semibold)' }}
            >
              {title}
            </h2>

            {/* Meta Info */}
            <div
              className="flex items-center gap-6 mb-6 text-muted-foreground flex-wrap"
              style={{ fontSize: 'var(--text-sm)' }}
            >
              {/* Course Type */}
              {offeringType && (
                <div className="flex items-center gap-2">
                  {getOfferingTypeIcon(offeringType)
                    .map(({ icon: IconComponent, color }) => (
                      <IconComponent key="offering-icon" color={color} size="xs" />
                    ))
                    .getOrElseValue(<></>)}
                  <span>{getOfferingTypeLabel(offeringType)}</span>
                </div>
              )}
              {hours > 0 && (
                <div className="flex items-center gap-2">
                  <ClockIcon className="w-4 h-4" />
                  <span>
                    <T kind="{{count}} hour" count={hours} />
                  </span>
                </div>
              )}
              {weeks > 0 && (
                <div className="flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4" />
                  <span>
                    <T kind="{{count}} week" count={weeks} />
                  </span>
                </div>
              )}
              {languages && languages.length > 0 && (
                <div className="flex items-center gap-2">
                  <GlobeAltIcon className="w-4 h-4" />
                  <span>{languages.join(', ')}</span>
                </div>
              )}
              {competencyList.length > 0 && (
                <div className="flex items-center gap-2">
                  <AcademicCapIcon className="w-4 h-4" />
                  <span>
                    <T kind="{{count}} Competency" count={competencyList.length} />
                  </span>
                </div>
              )}
            </div>

            {/* Unlicensed Badge */}
            {isUnlicensed && (
              <div className="mb-6">
                <span
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-100 text-amber-800 border border-amber-200 rounded"
                  style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}
                  title={translation(
                    "This course is not included in your organization's current licenses. You can add it to preview, but learners won't have access without purchasing the appropriate license.",
                  )(ts)}
                >
                  <LockClosedIcon className="w-4 h-4" />
                  <T kind="Unlicensed" />
                </span>
              </div>
            )}
            {payload.license_starts_on
              .map((startsOn) => (
                <div className="mb-6">
                  <span
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 border rounded ${FUTURE_LICENSE_TINT_CLASSES}`}
                    style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}
                    data-intercom-target="builder-v2-course-detail-future-license"
                  >
                    <CalendarIcon className="w-4 h-4" />
                    <LicenseStarts startsOn={startsOn} />
                  </span>
                </div>
              ))
              .getOrElseValue(<></>)}

            {/* Action Buttons */}
            <div className="flex items-center gap-3">
              {isAdded ? (
                <button
                  onClick={handleToggle}
                  className="px-6 py-2.5 border-2 border-accent text-accent rounded-md hover:bg-accent/10 transition-colors flex items-center gap-2"
                  style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}
                  data-intercom-target="builder-v2-remove-course-detail"
                >
                  <CheckCircleIcon className="w-6 h-6" />
                  <T kind="Added to this section" />
                </button>
              ) : (
                <button
                  onClick={handleToggle}
                  className="px-6 py-2.5 bg-accent text-accent-foreground rounded-md hover:opacity-90 transition-opacity flex items-center gap-2"
                  style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}
                  data-intercom-target="builder-v2-add-course-detail"
                >
                  <PlusIcon className="w-6 h-6" />
                  <T kind="Add to this section" />
                </button>
              )}

              {/* Download PDF Button */}
              {hasPdfDownload && (
                <button
                  onClick={handleDownload}
                  className="px-4 py-2.5 border border-border rounded-md hover:bg-muted transition-colors flex items-center gap-2"
                  style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}
                  aria-label={translation('Download course overview PDF')(ts)}
                  data-intercom-target="builder-v2-download-course-pdf-detail"
                >
                  <ArrowDownTrayIcon className="w-5 h-5" />
                  <T kind="Download PDF" />
                </button>
              )}
            </div>
          </div>

          {/* Video Preview - uses ExperienceVideoState which handles the HATEOAS
              fetch + HLS decode + player modal (same infrastructure the learner-facing
              list cards use). Renders nothing if the experience has no `thumbnail` rel. */}
          <div className="w-80 aspect-video flex-shrink-0">
            <ExperienceVideoState
              experienceResource={course as unknown as ExperienceResource}
              className="rounded-lg"
              hideEnrollAction
            />
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-8 py-6">
        {/* Description */}
        {description && (
          <div className="mb-8">
            <h4 className="mb-4">
              <T kind="Overview" />
            </h4>
            <div className="bg-card border border-border rounded-lg p-6">
              <p
                className="text-foreground whitespace-pre-line"
                style={{ fontSize: 'var(--text-sm)', lineHeight: '1.6' }}
              >
                {description}
              </p>
            </div>
          </div>
        )}

        {/* Competencies */}
        {competencyList.length > 0 && (
          <div className="mb-8">
            <h4 className="mb-4">
              <T kind="Competencies you will gain" />
            </h4>
            <div className="flex flex-wrap gap-2">
              {competencyList.map((competency) => (
                <span
                  key={competency}
                  className="px-2 py-0.5 bg-muted border border-border rounded"
                  style={{ fontSize: 'var(--text-xs)' }}
                >
                  {competency}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Scheduled Dates for Professor-Led Courses */}
        {scheduledDates.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h4>
                <T kind="Available Course Dates" />
              </h4>
              {selectedDateIds.size > 0 && (
                <span className="text-muted-foreground" style={{ fontSize: 'var(--text-sm)' }}>
                  <T
                    kind="{{selected}} of {{total}} selected"
                    selected={selectedDateIds.size}
                    total={scheduledDates.length}
                  />
                </span>
              )}
            </div>
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="space-y-1.5">
                {scheduledDates.map((date) => {
                  const isSelected = selectedDateIds.has(date.id);
                  return (
                    <label
                      key={date.id}
                      className="flex items-center gap-3 p-3 rounded-md hover:bg-muted cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleDateSelection(date.id)}
                        className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2 cursor-pointer"
                        data-intercom-target="builder-v2-select-course-date"
                      />
                      <span
                        className={isSelected ? 'text-foreground' : 'text-muted-foreground'}
                        style={{
                          fontSize: 'var(--text-sm)',
                          fontWeight: 'var(--font-weight-medium)',
                        }}
                      >
                        <L localizeable={date.start_on} format="long-weekday-month-year" />
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default observer(CourseDetailPanel);
